import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getLocation } from 'store/__router/selectors';

import { useHistory } from 'core/universalRouter/hooks/useHistory';

/**
 * Custom hooks to manage search params key-value pair as toggle state and add functionality
 * to toggle it from window back/forward history
 * @param {string} paramKey - search param query
 * @param {string} paramValue - search param value
 */
const useQueryToggle = (paramKey: string, paramValue: string) => {
  const location = useSelector(getLocation);
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);

  const [isOpen, setIsOpen] = useState(searchParams.getAll(paramKey).includes(paramValue));

  const onClose = () => {
    history?.goBack();
  };

  const onOpen = () => {
    const newSearchParams = new URLSearchParams(window.location.search);
    if (newSearchParams.has(paramKey) && !newSearchParams.getAll(paramKey).includes(paramValue)) {
      newSearchParams.append(paramKey, paramValue);
    } else {
      newSearchParams.set(paramKey, paramValue);
    }
    setIsOpen(true);
    history?.push({
      pathname: location.pathname,
      search: newSearchParams.toString(),
      state: {
        // to prevent re-rendering the whole page
        shouldUpdate: false,
      },
    });
  };

  useEffect(() => {
    const handlePopState = () => {
      const newSearchParams = new URLSearchParams(window.location.search);
      setIsOpen(newSearchParams.getAll(paramKey).includes(paramValue));
    };
    window.addEventListener('popstate', handlePopState);

    return function cleanUp() {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [paramKey, paramValue]);

  return { isOpen, onClose, onOpen };
};

export default useQueryToggle;
